import {
	Task,
	TaskContent,
	TaskItem,
	TaskTrigger,
} from "@/components/ai-elements/task";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
	CheckCircle2Icon,
	CheckIcon,
	CircleAlertIcon,
	Clock3Icon,
	LayoutGridIcon,
	MonitorIcon,
	XIcon,
} from "lucide-react";
import { useState } from "react";
import { AgentAvatar } from "./AgentAvatar.js";
import { decideApproval } from "./approvals-api.js";
import {
	AdsScorecardCard,
	parseHandoffSummary,
	SitesInventoryCard,
} from "./HandoffSummary.js";
import type { ChatHandoff, HandoffDecisionEvent } from "./messages.js";
import { AGENTS, type AgentId } from "./nav.js";

type Props = {
	handoffs: ChatHandoff[];
	onOpenAgent?: (id: AgentId) => void;
	onOpenSitePreview?: (url: string) => void;
	onHandoffPatch?: (handoffId: string, patch: Partial<ChatHandoff>) => void;
	onHandoffDecision?: (event: HandoffDecisionEvent) => void;
	/**
	 * `work` — inventory / tool results (sit above prose, collapsed when done).
	 * `gate` — pending Approve/Reject only (sit below prose as the final CTA).
	 */
	slot?: "work" | "gate";
};

function previewUrlFor(handoff: ChatHandoff): string | null {
	if (handoff.previewUrl?.startsWith("http")) return handoff.previewUrl;
	const fromSummary = handoff.summary?.match(/siteUrl=(https?:\/\/\S+)/i)?.[1];
	return fromSummary ?? null;
}

export function needsApproval(handoff: ChatHandoff): boolean {
	return (
		Boolean(handoff.approvalId) &&
		handoff.status === "pending" &&
		!handoff.decision
	);
}

function statusBadge(handoff: ChatHandoff) {
	if (handoff.decision === "approved") {
		return (
			<span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-emerald-500">
				<CheckCircle2Icon className="size-3.5" />
				Approved
			</span>
		);
	}
	if (handoff.decision === "rejected") {
		return (
			<span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-destructive">
				<XIcon className="size-3.5" />
				Rejected
			</span>
		);
	}
	if (handoff.phase === "started" || handoff.phase === "progress") {
		return (
			<span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-mx-quaternary">
				<Spinner className="size-3" />
				Working…
			</span>
		);
	}
	if (needsApproval(handoff)) {
		return (
			<span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-amber-500">
				<Clock3Icon className="size-3.5" />
				Needs approval
			</span>
		);
	}
	switch (handoff.status) {
		case "completed":
			return (
				<span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-emerald-500">
					<CheckCircle2Icon className="size-3.5" />
					Done
				</span>
			);
		case "pending":
			return (
				<span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-amber-500">
					<Clock3Icon className="size-3.5" />
					In progress
				</span>
			);
		case "unavailable":
			return (
				<span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-mx-quaternary">
					Coming soon
				</span>
			);
		case "failed":
			return (
				<span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-destructive">
					<CircleAlertIcon className="size-3.5" />
					Failed
				</span>
			);
		default:
			return (
				<span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-mx-quaternary">
					<Spinner className="size-3" />
					Working…
				</span>
			);
	}
}

function resolveAgent(handoff: ChatHandoff) {
	const id = handoff.openAgentId ?? handoff.agentType;
	const fromRoster = AGENTS.find((row) => row.id === id);
	if (fromRoster) return fromRoster;
	return {
		name: handoff.agentLabel,
		initials: handoff.agentLabel.slice(0, 2).toUpperCase(),
		avatar: null,
		avatar2x: null,
	};
}

/** Drop machine bits the card already surfaces (approvalId, siteUrl). */
function displaySummary(summary: string): string {
	return summary
		.replace(/\s*—\s*approvalId=[^\s—]+/gi, "")
		.replace(/\s*—\s*siteUrl=https?:\/\/\S+/gi, "")
		.replace(/\s*·\s*decision=\S+/gi, "")
		.replace(/\s*·\s*asOf=\S+/gi, "")
		.replace(/\s{2,}/g, " ")
		.trim();
}

/** What the agent last saw in its browser — proof of the visual check. */
function HandoffScreenshot({ handoff }: { handoff: ChatHandoff }) {
	if (!handoff.screenshotUrl) return null;
	return (
		<figure className="mc-handoff-shot">
			<img
				src={handoff.screenshotUrl}
				alt={`${handoff.agentLabel}'s view of the site`}
				loading="lazy"
			/>
			<figcaption>
				{handoff.phase === "done"
					? `${handoff.agentLabel} checked the result in a browser`
					: `${handoff.agentLabel} is looking at the page`}
			</figcaption>
		</figure>
	);
}

function HandoffBody({ handoff }: { handoff: ChatHandoff }) {
	if (!handoff.summary) {
		return (
			<>
				<p className="mc-handoff-task">{handoff.task}</p>
				{handoff.phase !== "done" && handoff.note ? (
					<p className="mc-handoff-note flex items-center gap-1.5 text-[12px] text-mx-tertiary">
						<Spinner className="size-3 shrink-0" />
						{handoff.note}
					</p>
				) : null}
				<HandoffScreenshot handoff={handoff} />
			</>
		);
	}

	const parsed = parseHandoffSummary(handoff.summary);
	if (parsed.kind === "sites") {
		return (
			<>
				<p className="mc-handoff-task">{handoff.task}</p>
				<SitesInventoryCard
					nodes={parsed.nodes}
					notes={parsed.notes}
					asOf={parsed.asOf}
				/>
			</>
		);
	}
	if (parsed.kind === "ads") {
		return (
			<>
				<p className="mc-handoff-task">{handoff.task}</p>
				<AdsScorecardCard card={parsed.card} asOf={parsed.asOf} />
			</>
		);
	}

	return (
		<>
			<p className="mc-handoff-task">{handoff.task}</p>
			<p className="mc-handoff-prose">{displaySummary(parsed.text)}</p>
			<HandoffScreenshot handoff={handoff} />
		</>
	);
}

function ApprovalActions({
	handoff,
	onHandoffPatch,
	onHandoffDecision,
}: {
	handoff: ChatHandoff;
	onHandoffPatch?: (handoffId: string, patch: Partial<ChatHandoff>) => void;
	onHandoffDecision?: (event: HandoffDecisionEvent) => void;
}) {
	const [busy, setBusy] = useState<"approve" | "reject" | null>(null);

	if (!handoff.approvalId) return null;

	if (handoff.decision) {
		return (
			<p className="text-[12px] text-mx-tertiary">
				{handoff.decision === "approved"
					? "Approved — confirmation coming up."
					: "Rejected — confirmation coming up."}
			</p>
		);
	}

	if (handoff.status !== "pending") return null;

	const decide = async (verdict: "approve" | "reject") => {
		if (!handoff.approvalId || busy) return;
		setBusy(verdict);
		const result = await decideApproval({
			approvalId: handoff.approvalId,
			verdict,
		});
		setBusy(null);
		if (!result.ok) {
			onHandoffPatch?.(handoff.handoffId, {
				decisionError: result.error,
			});
			return;
		}
		const patch: Partial<ChatHandoff> = {
			decision: verdict === "approve" ? "approved" : "rejected",
			status: "completed",
			decisionError: undefined,
			summary: [
				handoff.summary,
				verdict === "approve"
					? "You approved this in chat."
					: "You rejected this in chat.",
			]
				.filter(Boolean)
				.join(" — "),
		};
		if (onHandoffDecision) {
			onHandoffDecision({
				handoffId: handoff.handoffId,
				verdict,
				handoff,
				patch,
			});
		} else {
			onHandoffPatch?.(handoff.handoffId, patch);
		}
	};

	return (
		<div className="mc-handoff-gate flex flex-col gap-2 rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2.5">
			<p className="text-[12px] font-medium text-amber-200/90">
				Needs your approval — publicly visible once approved.
			</p>
			{handoff.decisionError ? (
				<p className="text-[12px] text-destructive">{handoff.decisionError}</p>
			) : null}
			<div className="flex flex-wrap gap-2">
				<Button
					type="button"
					size="sm"
					className="h-8 gap-1.5 bg-emerald-600 text-[12px] text-white hover:bg-emerald-500"
					disabled={busy !== null}
					onClick={() => void decide("approve")}
				>
					{busy === "approve" ? (
						<Spinner className="size-3.5" />
					) : (
						<CheckIcon className="size-3.5" />
					)}
					Approve
				</Button>
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="h-8 gap-1.5 text-[12px]"
					disabled={busy !== null}
					onClick={() => void decide("reject")}
				>
					{busy === "reject" ? (
						<Spinner className="size-3.5" />
					) : (
						<XIcon className="size-3.5" />
					)}
					Reject
				</Button>
			</div>
		</div>
	);
}

/** Compact front-desk → specialist handoff cards with persona avatars. */
export function HandoffCards({
	handoffs,
	onOpenAgent,
	onOpenSitePreview,
	onHandoffPatch,
	onHandoffDecision,
	slot = "work",
}: Props) {
	const visible = handoffs.filter((handoff) =>
		slot === "gate" ? needsApproval(handoff) : !needsApproval(handoff),
	);
	if (visible.length === 0) return null;

	return (
		<div
			className={
				slot === "gate"
					? "mc-handoffs mb-2 mt-2.5 flex w-full flex-col gap-2"
					: "mc-handoffs mb-2 flex w-full flex-col gap-2"
			}
		>
			{visible.map((handoff) => {
				const agent = resolveAgent(handoff);
				const title = `${handoff.agentLabel} · ${handoff.task}`;
				const previewUrl = previewUrlFor(handoff);
				const canOpen =
					Boolean(handoff.openAgentId) &&
					Boolean(onOpenAgent) &&
					(handoff.status === "pending" ||
						handoff.status === "unavailable" ||
						handoff.phase === "done" ||
						Boolean(handoff.decision));
				/** Work results collapse when done so the reply stays primary. */
				const defaultOpen =
					slot === "gate" ||
					handoff.phase === "started" ||
					handoff.phase === "progress" ||
					(slot === "work" &&
						handoff.status !== "completed" &&
						!handoff.decision);

				return (
					<Task
						key={handoff.handoffId}
						defaultOpen={defaultOpen}
						className="mc-handoff rounded-xl border border-mx-sep bg-mx-elevated/70 px-3 py-2.5"
					>
						<TaskTrigger title={title}>
							<div className="flex w-full cursor-pointer items-center gap-2.5 text-mx-secondary text-sm transition-colors hover:text-mx-label">
								<AgentAvatar agent={agent} size="sm" />
								<span className="min-w-0 flex-1 truncate font-medium text-mx-label">
									{handoff.agentLabel}
								</span>
								<span className="hidden min-w-0 truncate text-[12px] text-mx-quaternary sm:inline">
									{handoff.task}
								</span>
								{statusBadge(handoff)}
							</div>
						</TaskTrigger>
						<TaskContent className="mc-handoff-body mt-2.5 space-y-2.5 border-l-0 pl-0">
							<TaskItem className="!p-0 !text-inherit">
								<HandoffBody handoff={handoff} />
							</TaskItem>
							{slot === "gate" ? (
								<ApprovalActions
									handoff={handoff}
									onHandoffPatch={onHandoffPatch}
									onHandoffDecision={onHandoffDecision}
								/>
							) : null}
							<div className="flex flex-wrap gap-2">
								{previewUrl && onOpenSitePreview ? (
									<Button
										type="button"
										variant="outline"
										size="sm"
										className="h-7 gap-1.5 text-[12px]"
										onClick={() => onOpenSitePreview(previewUrl)}
									>
										<MonitorIcon className="size-3.5" />
										Preview site
									</Button>
								) : null}
								{canOpen && handoff.openAgentId ? (
									<Button
										type="button"
										variant="outline"
										size="sm"
										className="h-7 gap-1.5 text-[12px]"
										onClick={() =>
											onOpenAgent?.(handoff.openAgentId as AgentId)
										}
									>
										<LayoutGridIcon className="size-3.5" />
										Open Desk
									</Button>
								) : null}
							</div>
						</TaskContent>
					</Task>
				);
			})}
		</div>
	);
}
